// User-facing Requests Portal.
//
// Chromeless page at /requests for any L&T staff member (Entra-
// authenticated, no IT-group check). Also embedded in the IT admin app
// under sidebar > User Requests so IT can see + submit through the same
// UI users see.
//
// Design shape:
//   Landing view      → tile picker for each request TYPE + user's own
//                       recent requests
//   Type-specific form → dedicated form for the chosen type
//   Success view      → confirmation + link back to landing
//
// Why per-type forms (not one form with a radio): request types have
// meaningfully different fields, and mixing them into one form with
// show/hide conditionals turns into unreadable spaghetti as more types
// get added. Each type gets its own component; the router below picks
// which one to render based on `mode`.

import React from 'react';
import { AiRequestsBoard } from './ai-requests.jsx';

const { useState, useMemo, useEffect, useCallback } = React;

// AI Requests is gated to this allowlist while it's in testing - only these
// people see the portal tile, and the backend rejects everyone else too
// (see AI_REQUESTS_ALLOWED in functions/src/index.js). Keep the two in sync.
const AI_REQUESTS_ALLOWED = [
  'jamesclydesdale@leeandthompson.com',
  'robhilton@leeandthompson.com',
  'andymoseby@leeandthompson.com',
  'simonboyden@leeandthompson.com',
  'samgoodman@leeandthompson.com',
];
const canSeeAiRequests = (email) => AI_REQUESTS_ALLOWED.includes(String(email || '').toLowerCase());
// Admins (change status + delete). Keep in sync with AI_REQUESTS_ADMINS in
// functions/src/index.js - the backend enforces it; this only shows/hides the
// admin controls in the UI.
const AI_REQUESTS_ADMINS = ['jamesclydesdale@leeandthompson.com', 'robhilton@leeandthompson.com'];
const isAiAdmin = (email) => AI_REQUESTS_ADMINS.includes(String(email || '').toLowerCase());

// Self-service ticket submission - allowlist-gated for a phased rollout (just
// james for now). Mirrors TICKET_SUBMIT_ALLOWED in functions/src/index.js; the
// backend is gated too, so hiding the tile isn't the security. To open it to the
// firm, expand both lists (or drop the gate).
const TICKET_SUBMIT_ALLOWED = ['jamesclydesdale@leeandthompson.com', 'robhilton@leeandthompson.com'];
const canSubmitTicket = (email) => TICKET_SUBMIT_ALLOWED.includes(String(email || '').toLowerCase());

// Shared API caller - same window.IT_AUTH bridge every other screen uses.
const reqApi = (method, path, body) => window.IT_AUTH.api(method, path, body, 'requests');

// Registered request types. Adding a new type = new entry here + new
// form component + case in RequestForm below. Nothing else needs
// touching - sidebar tile grid + admin queue picks up the new type
// automatically.
const REQUEST_TYPES = [
  {
    id: 'file-access',
    label: 'File Access',
    icon: '📁',
    blurb: 'Request access to files for a specific client / matter.',
    // The value written to the SharePoint list RequestType column when
    // Phase 1b wires the POST. Must match what the Power Automate flow
    // trigger filters on ("File Access" for L&T009).
    sharepointValue: 'File Access',
  },
  {
    id: 'info-barrier',
    label: 'Information Barrier',
    icon: '🚧',
    blurb: 'Restrict a client / matter so only named users can see the files.',
    sharepointValue: 'Information Barrier',
  },
];

// Public entry point. `user` = MSAL profile from Root ({ displayName,
// mail, userPrincipalName }). `embedded` = true when rendered inside
// the admin app shell (drops the chromeless full-viewport wrap + the
// portal-only header brand row).
// The proper Triage lockup - same SVG as the sidebar in the admin app,
// re-coloured for a light background (TRI teal, AGE dark, subtitle grey).
// Small enough to sit in the portal header alongside a Back link.
function TriageLogo() {
  return (
    <svg viewBox="0 0 240 96" xmlns="http://www.w3.org/2000/svg"
         role="img" aria-label="Triage" style={{ height: 40 }}>
      <rect x="24" y="24" width="30" height="23" rx="5" fill="#0D9488" fillOpacity="0.35"/>
      <rect x="16" y="34" width="30" height="23" rx="5" fill="#0D9488"/>
      <line x1="22" y1="44" x2="42" y2="44" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round"/>
      <line x1="22" y1="50" x2="34" y2="50" stroke="#FFFFFF" strokeWidth="2.4" strokeLinecap="round"/>
      <text x="66" y="52" fontFamily="Poppins, 'Segoe UI', Helvetica, Arial, sans-serif"
            fontSize="32" fontWeight="700" letterSpacing="2">
        <tspan fill="#0D9488">TRI</tspan>
        <tspan fill="#1F2937">AGE</tspan>
      </text>
      <text x="68" y="70" fontFamily="Poppins, 'Segoe UI', Helvetica, Arial, sans-serif"
            fontSize="11" fontWeight="500" letterSpacing="3" fill="#6B7280">IT Helpdesk</text>
    </svg>
  );
}

// Line icons for the two request types - replaces the 📁/🚧 emoji, which
// were the portal's most template-generic tell. Same 20x20 / stroke-1.6
// style as the main app's Icon set so the portal reads as part of Triage,
// not a bolted-on form. Folder = File Access; shield+keyhole = the
// restricted-access nature of an Information Barrier.
function TypeIcon({ type, size = 20 }) {
  const paths = {
    'file-access': (
      <path d="M2.5 5.5A1.5 1.5 0 0 1 4 4h3.1l1.6 1.9H16A1.5 1.5 0 0 1 17.5 7.4v6.6A1.5 1.5 0 0 1 16 15.5H4A1.5 1.5 0 0 1 2.5 14V5.5Z"/>
    ),
    'info-barrier': (
      <>
        <path d="M10 2.5 4.5 4.8v4.4c0 3.4 2.3 6 5.5 7.3 3.2-1.3 5.5-3.9 5.5-7.3V4.8L10 2.5Z"/>
        <circle cx="10" cy="8.8" r="1.3"/>
        <path d="M10 10.1v2.3"/>
      </>
    ),
  };
  return (
    <svg viewBox="0 0 20 20" width={size} height={size} fill="none"
         stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      {paths[type] || null}
    </svg>
  );
}

export function RequestsPortal({ user, embedded = false }) {
  // Local view state: 'landing' shows the type picker, else a type id
  // ('file-access' / 'info-barrier') shows that type's form, or
  // 'success:<type-id>' shows the post-submit confirmation.
  const [mode, setMode] = useState('landing');
  // Submitter's own request history. Loaded on mount + refetched every
  // time we return to the landing view (after a successful submit) so
  // the new entry shows up without a full page refresh.
  const [mine, setMine] = useState(null); // null = loading, [] = empty, [...] = populated
  const [mineError, setMineError] = useState(null);

  const loadMine = useCallback(async () => {
    setMineError(null);
    try { setMine(await reqApi('GET', '/requests/mine') || []); }
    catch (e) { setMineError(e.message || 'Failed to load'); setMine([]); }
  }, []);
  useEffect(() => { loadMine(); }, [loadMine]);
  // Also refetch when returning to landing after a submit.
  useEffect(() => { if (mode === 'landing') loadMine(); }, [mode, loadMine]);

  const submitterName  = user?.displayName || 'Unknown';
  const submitterEmail = user?.mail || user?.userPrincipalName || '';

  const signOut = () => {
    try { window.IT_AUTH?.signOut?.(); }
    catch (_) { /* fall through */ }
  };

  const goHome = () => setMode('landing');

  // AI Requests visibility - allowlist only, while it's in testing. Others
  // never get the tile, and the `&& isAiUser` guard below means even a forced
  // mode falls back to the landing (and the backend rejects them regardless).
  const isAiUser = canSeeAiRequests(submitterEmail);
  // Self-service ticket submission - allowlist only while in testing (same
  // pattern as AI Ideas). Backend is gated too.
  const canTicket = canSubmitTicket(submitterEmail);

  let content;
  if (mode === 'landing') {
    content = <Landing onPick={setMode} mine={mine} mineError={mineError} canSeeAi={isAiUser} canTicket={canTicket}/>;
  } else if (mode === 'ai-requests' && isAiUser) {
    content = <AiRequestsBoard isAdmin={isAiAdmin(submitterEmail)}/>;
  } else if (mode === 'submit-ticket' && canTicket) {
    content = <SubmitTicketForm onSubmitted={(id) => setMode(`ticket-success:${id}`)}/>;
  } else if (mode.startsWith('ticket-success:')) {
    content = <TicketSuccess id={mode.slice('ticket-success:'.length)} onNew={goHome}/>;
  } else if (mode.startsWith('success:')) {
    const type = REQUEST_TYPES.find(t => t.id === mode.slice('success:'.length));
    content = <Success type={type} onNew={goHome}/>;
  } else {
    const type = REQUEST_TYPES.find(t => t.id === mode);
    if (!type) { content = <Landing onPick={setMode} mine={mine} mineError={mineError} canSeeAi={isAiUser} canTicket={canTicket}/>; }
    else content = (
      <RequestForm
        type={type}
        user={user}
        onSubmitted={() => setMode(`success:${type.id}`)}
      />
    );
  }

  // Show a Back link at the top-right of the card whenever the user is
  // NOT on the landing tile grid. Sits where the sign-out button used
  // to; keeps the header row balanced and gives Back a clear, expected
  // spot. Sign-out removed entirely - this is an internal app on the
  // tenant SSO, users don't need to sign out from it.
  const showBack = mode !== 'landing' && !mode.startsWith('success:') && !mode.startsWith('ticket-success:');

  return (
    <div style={embedded ? styles.pageEmbedded : styles.page}>
      {/* AI Ideas gets a much wider card so it reads as a full page rather than
          a small centred box; every other portal view keeps the 720px card. */}
      <div style={mode === 'ai-requests' ? { ...styles.card, maxWidth: 1200 } : styles.card}>
        {!embedded && (
          <div style={styles.brandRow}>
            <div style={styles.brand}>
              {showBack && (
                <button type="button" onClick={goHome} style={styles.backLink}>
                  ← Back
                </button>
              )}
              <TriageLogo/>
            </div>
            <div style={styles.userChip} title={submitterEmail}>
              <span style={styles.userName}>{submitterName}</span>
            </div>
          </div>
        )}
        {content}
      </div>
    </div>
  );
}

// ── Landing: type picker + user's recent requests ─────────────────────────
function Landing({ onPick, mine, mineError, canSeeAi, canTicket }) {
  // Recent requests collapsed by default so the landing stays focused on
  // the submit tiles - the header shows the count and expands on click.
  const [open, setOpen] = useState(false);
  const count = Array.isArray(mine) ? mine.length : null;

  // Row/section styling for the grouped picker (variant C). Defined here (not at
  // module top) so it can read the T theme object, which is declared lower down.
  const rowSt = {
    label: { fontSize: 11, fontWeight: 700, letterSpacing: '0.07em', color: '#9CA3AF', textTransform: 'uppercase', margin: '0 0 10px' },
    list:  { border: `1px solid ${T.border}`, borderRadius: 14, overflow: 'hidden' },
    row:   { display: 'flex', alignItems: 'center', gap: 14, padding: '15px 18px', width: '100%', background: 'transparent', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' },
    icon:  { color: '#0D9488', flexShrink: 0, display: 'flex' },
    title: { fontSize: 14, fontWeight: 600, color: T.text, display: 'block' },
    blurb: { fontSize: 12.5, color: T.muted, marginTop: 1, display: 'block' },
    chev:  { color: '#C7C4C1', flexShrink: 0, display: 'flex' },
  };
  const ticketIcon = (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 5.5A1.5 1.5 0 0 1 4.5 4h11A1.5 1.5 0 0 1 17 5.5v9a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 3 14.5z"/>
      <path d="M6.5 8.5h7M6.5 11.5h4"/>
    </svg>
  );
  const sparkle = (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 3v3M10 14v3M3 10h3M14 10h3M5.5 5.5l2 2M12.5 12.5l2 2M5.5 14.5l2-2M12.5 7.5l2-2"/>
    </svg>
  );
  // Grouped request picker. Empty groups (e.g. the allowlisted ones for a user
  // who isn't allowed) are filtered out entirely. FA/IB/AI blurbs unchanged.
  const groups = [
    { label: 'Support', items: canTicket ? [{ key: 'submit-ticket', icon: ticketIcon, title: 'Log an IT ticket', blurb: 'Report a problem or ask IT for something.' }] : [] },
    { label: 'Access & matters', items: REQUEST_TYPES.map(t => ({ key: t.id, icon: <TypeIcon type={t.id}/>, title: t.label, blurb: t.blurb })) },
    { label: 'Ideas', items: canSeeAi ? [{ key: 'ai-requests', icon: sparkle, title: 'AI Ideas', blurb: 'Submit and vote on ideas for how AI could help the firm.' }] : [] },
  ].filter(g => g.items.length);

  return (
    <div style={styles.section}>
      <h1 style={styles.h1}>Submit a request</h1>
      <p style={styles.subtle}>
        Choose the type of request you want to submit below.
      </p>

      {groups.map((g, gi) => (
        <div key={g.label} style={{ marginTop: gi === 0 ? 22 : 18 }}>
          <div style={rowSt.label}>{g.label}</div>
          <div style={rowSt.list}>
            {g.items.map((it, i) => (
              <button key={it.key} type="button" onClick={() => onPick(it.key)}
                style={{ ...rowSt.row, borderTop: i === 0 ? 'none' : `1px solid ${T.border}` }}>
                <span style={rowSt.icon}>{it.icon}</span>
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={rowSt.title}>{it.title}</span>
                  <span style={rowSt.blurb}>{it.blurb}</span>
                </span>
                <span style={rowSt.chev}>
                  <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M7 4l6 6-6 6"/></svg>
                </span>
              </button>
            ))}
          </div>
        </div>
      ))}

      <div style={styles.recentBlock}>
        <button type="button" style={styles.recentToggle} onClick={() => setOpen(o => !o)}>
          <span style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 120ms', display: 'inline-block' }}>▸</span>
          Your recent requests
          {count !== null && <span style={styles.recentCount}>{count}</span>}
        </button>
        {open && (
          <div style={{ marginTop: 10 }}>
            {mineError && (
              <div style={{ ...styles.recentEmpty, color: '#DC2626', fontStyle: 'normal' }}>
                Couldn't load your requests: {mineError}
              </div>
            )}
            {!mineError && mine === null && (
              <div style={styles.recentEmpty}>Loading…</div>
            )}
            {!mineError && Array.isArray(mine) && mine.length === 0 && (
              <div style={styles.recentEmpty}>
                Once you've submitted requests, you'll see their status here.
              </div>
            )}
            {!mineError && Array.isArray(mine) && mine.length > 0 && (
              <div style={styles.mineList}>
                {mine.slice(0, 10).map(r => <MineRow key={`${r.kind || 'request'}-${r.id}`} r={r}/>)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Compact row for the "your recent requests" list. Left-aligned label,
// right-aligned status pill + date. Handles two kinds of item: File Access /
// Info Barrier requests, and self-service IT tickets (kind === 'ticket').
function MineRow({ r }) {
  if (r.kind === 'ticket') {
    return (
      <div style={styles.mineRow}>
        <div style={styles.mineRowMain}>
          <div style={styles.mineRowTitle}>
            IT ticket <span style={styles.mineRowCM}>#{r.id} · {r.subject}</span>
          </div>
          <div style={styles.mineRowMeta}>{fmtDate(r.createdAt)}</div>
        </div>
        <TicketStatusPill status={r.status}/>
      </div>
    );
  }
  const label = (REQUEST_TYPES.find(t => t.id === r.type)?.label) || r.type;
  const nameSuffix = (r.clientDescription || r.matterDescription)
    ? ` · ${r.clientDescription || '-'}${r.matterDescription ? ` / ${r.matterDescription}` : ''}`
    : '';
  return (
    <div style={styles.mineRow}>
      <div style={styles.mineRowMain}>
        <div style={styles.mineRowTitle}>
          {label} <span style={styles.mineRowCM}>{r.clientNumber}.{r.matterNumber}{nameSuffix}</span>
        </div>
        <div style={styles.mineRowMeta}>
          {r.decisionNote && r.status !== 'pending' && (
            <span style={{ color: T.muted, fontStyle: 'italic' }}>”{r.decisionNote}” - </span>
          )}
          {r.status !== 'pending' && r.reviewedByName ? `${r.reviewedByName} · ` : ''}
          {fmtDate(r.reviewedAt || r.createdAt)}
        </div>
      </div>
      <StatusPill status={r.status}/>
    </div>
  );
}

function StatusPill({ status }) {
  const cfg = STATUS_CFG[status] || STATUS_CFG.pending;
  return <span style={{ ...styles.pill, background: cfg.bg, color: cfg.fg }}>{cfg.label}</span>;
}

// Ticket workflow statuses (Tickets table) differ from request statuses, so
// they get their own pill mapping. Mirrors the labels used in the IT queue.
const TICKET_STATUS_CFG = {
  new:      { label: 'New',         bg: '#DBEAFE', fg: '#1E40AF' },
  progress: { label: 'In progress', bg: '#FEF3C7', fg: '#92400E' },
  waiting:  { label: 'Waiting',     bg: '#FEF3C7', fg: '#92400E' },
  resolved: { label: 'Closed',      bg: '#D1FAE5', fg: '#065F46' },
};
function TicketStatusPill({ status }) {
  const cfg = TICKET_STATUS_CFG[status] || TICKET_STATUS_CFG.new;
  return <span style={{ ...styles.pill, background: cfg.bg, color: cfg.fg }}>{cfg.label}</span>;
}

const STATUS_CFG = {
  pending:        { label: 'Pending',   bg: '#FEF3C7', fg: '#92400E' },
  approved:       { label: 'Approved',  bg: '#D1FAE5', fg: '#065F46' },
  rejected:       { label: 'Rejected',  bg: '#FEE2E2', fg: '#991B1B' },
  completed:      { label: 'Completed', bg: '#D1FAE5', fg: '#065F46' },
  'completed-na': { label: 'N/A',        bg: '#E5E7EB', fg: '#374151' },
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
  } catch (_) { return ''; }
};

// ── Success: post-submit confirmation ──────────────────────────────────────
function Success({ type, onNew }) {
  return (
    <div style={styles.section}>
      <div style={styles.successIcon}>✓</div>
      <h1 style={styles.h1}>Request submitted</h1>
      <p style={styles.subtle}>
        Your {type?.label || 'request'} has been sent to the IT team. You'll receive an
        email once it's been reviewed.
      </p>
      <div style={{ marginTop: 24 }}>
        <button type="button" onClick={onNew} style={styles.submit}>
          Submit another request
        </button>
      </div>
    </div>
  );
}

// ── Log an IT ticket: self-service intake straight into the helpdesk ───────
// Just a subject + details. Every portal ticket is filed under the fixed
// "Requests Portal" category server-side (no category/priority picker), so IT
// can see at a glance which tickets came from self-service. The requester
// identity is also set server-side from the token, so the form never sends it.
const errBox = { background: '#FEF2F2', border: '1px solid #FECACA', color: '#991B1B', padding: '8px 12px', borderRadius: 6, fontSize: 13 };
function SubmitTicketForm({ onSubmitted }) {
  const [form, setForm] = useState({ subject: '', description: '' });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const canSubmit = !!form.subject.trim() && !!form.description.trim() && !submitting;

  const submit = async () => {
    if (!canSubmit) return;
    setSubmitting(true); setError(null);
    try {
      const res = await reqApi('POST', '/requests/ticket', {
        subject: form.subject.trim(),
        description: form.description.trim(),
      });
      onSubmitted(res?.id);
    } catch (e) {
      setError(e.message || 'Could not submit. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.section}>
      <h1 style={styles.h1}>Log an IT ticket</h1>
      <p style={styles.subtle}>Tell us what you need and IT will pick it up. Replies come from helpdesk@leeandthompson.com.</p>

      <div style={styles.field}>
        <label style={styles.label}>What do you need? <span style={{ color: '#DC2626' }}>*</span></label>
        <input style={styles.input} value={form.subject} onChange={e => set('subject', e.target.value)}
          placeholder="A short summary of what you need" maxLength={200}/>
      </div>

      <div style={styles.field}>
        <label style={styles.label}>Details <span style={{ color: '#DC2626' }}>*</span></label>
        <textarea style={styles.textarea} value={form.description} onChange={e => set('description', e.target.value)}
          placeholder="Describe the problem in as much detail as you can." rows={5} maxLength={10000}/>
      </div>

      {error && <div style={errBox}>{error}</div>}

      <div style={styles.submitRow}>
        <button type="button" onClick={submit} disabled={!canSubmit}
          style={{ ...styles.submit, ...(!canSubmit ? styles.submitDisabled : {}) }}>
          {submitting ? 'Submitting…' : 'Submit ticket'}
        </button>
      </div>
    </div>
  );
}

function TicketSuccess({ id, onNew }) {
  return (
    <div style={styles.section}>
      <div style={styles.successIcon}>✓</div>
      <h1 style={styles.h1}>Ticket logged{id ? ` #${id}` : ''}</h1>
      <p style={styles.subtle}>
        Thanks, your request is in the IT helpdesk{id ? ` as ticket #${id}` : ''}. One of the IT team will
        get back to you shortly (●'◡'●)
      </p>
      <div style={{ marginTop: 24 }}>
        <button type="button" onClick={onNew} style={styles.submit}>Log another ticket</button>
      </div>
    </div>
  );
}

// ── Request form: dispatches on type id to the right form component ────────
function RequestForm({ type, user, onSubmitted }) {
  // No local Back button - the portal's top header renders one on the
  // right of the brand row whenever we're inside a form.
  const header = (
    <div style={styles.formHeader}>
      <div style={styles.formType}>
        <span style={styles.formTypeIcon}><TypeIcon type={type.id} size={18}/></span>
        {type.label}
      </div>
    </div>
  );

  if (type.id === 'file-access') {
    return <>{header}<FileAccessForm user={user} onSubmitted={onSubmitted}/></>;
  }
  if (type.id === 'info-barrier') {
    return <>{header}<InfoBarrierForm user={user} onSubmitted={onSubmitted}/></>;
  }
  return <>{header}<div style={styles.formHelp}>Unknown request type.</div></>;
}

// ── Shared field primitives ────────────────────────────────────────────────
// Kept here rather than in components.jsx so the portal file stays self-
// contained - a user hitting /requests should not need any other module
// beyond React itself to render.

function TextField({ label, help, value, onChange, type = 'text', placeholder, autoNumeric, maxLength }) {
  const change = e => {
    let v = e.target.value;
    if (autoNumeric) v = v.replace(/\D/g, '');
    if (maxLength && v.length > maxLength) v = v.slice(0, maxLength);
    onChange(v);
  };
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      <input
        type={type}
        inputMode={autoNumeric ? 'numeric' : undefined}
        autoComplete="off"
        value={value}
        onChange={change}
        placeholder={placeholder}
        style={styles.input}
        maxLength={maxLength}
      />
      {help && <div style={styles.help}>{help}</div>}
    </div>
  );
}

function TextArea({ label, help, value, onChange, placeholder, rows = 3 }) {
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      <textarea
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        style={styles.textarea}
      />
      {help && <div style={styles.help}>{help}</div>}
    </div>
  );
}

// Multi-select people picker - chip-style input. Placeholder until the
// backend Graph proxy is wired; the real picker will fill this same slot.
function PeoplePicker({ label, help, values, onChange, placeholder }) {
  const [draft, setDraft] = useState('');
  const commit = () => {
    const email = draft.trim().toLowerCase();
    if (!email || !email.includes('@')) return;
    if (values.some(v => v.email.toLowerCase() === email)) { setDraft(''); return; }
    onChange([...values, { email, displayName: email.split('@')[0] }]);
    setDraft('');
  };
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      <div style={styles.pickerChips}>
        {values.map((v, i) => (
          <span key={v.email} style={styles.chip}>
            {v.displayName || v.email}
            <button
              type="button"
              aria-label={`Remove ${v.displayName || v.email}`}
              onClick={() => onChange(values.filter((_, j) => j !== i))}
              style={styles.chipX}
            >×</button>
          </span>
        ))}
        <input
          type="email"
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); commit(); }
            if (e.key === 'Backspace' && !draft && values.length) onChange(values.slice(0, -1));
          }}
          onBlur={commit}
          placeholder={values.length ? '' : (placeholder || 'name@leeandthompson.com')}
          style={styles.pickerInput}
        />
      </div>
      {help && <div style={styles.help}>{help}</div>}
    </div>
  );
}

// Single-value equivalent - same placeholder shape.
function SinglePicker({ label, help, value, onChange, placeholder }) {
  const [draft, setDraft] = useState(value ? (value.displayName || value.email) : '');
  const commit = () => {
    const raw = draft.trim();
    if (!raw) { onChange(null); return; }
    if (raw.includes('@')) onChange({ email: raw.toLowerCase(), displayName: raw.split('@')[0] });
    else onChange({ email: '', displayName: raw });
  };
  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      <input
        type="text"
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        placeholder={placeholder || 'Type a name'}
        style={styles.input}
      />
      {help && <div style={styles.help}>{help}</div>}
    </div>
  );
}

// ── Entra-backed group pickers ────────────────────────────────────────────
// Fetch the group's members once via MS Graph, cache per browser session,
// filter client-side by name/email as the user types. Uses the existing
// MSAL token which already carries the GroupMember.Read.All scope - no
// new permissions needed. Returns { id, email, displayName } - shape is
// backward-compatible with the previous stub picker so form validation,
// backend submit, and email-match at approve time are unchanged.

// L&T Entra group Object IDs - verified with James 22 Jul 2026.
const ENTRA_GROUP_ALL_PARTNERS    = '6a89d035-7497-4eab-a946-de36d80b6d82';
const ENTRA_GROUP_ALL_FEE_EARNERS = '32d59d16-4eee-4be6-bb16-faf48431738b';
const ENTRA_GROUP_LEGAL_SUPPORT   = '14b61dfe-6a00-4e77-a74f-0f56c6aab3d0';
const ENTRA_GROUP_BUSINESS_SVCS   = '925cee27-6b9f-4399-836b-d5bc997ddf18';
// The "Access granted to" picker draws from fee earners + legal support +
// business services so support staff can be granted access alongside fee
// earners. Members are merged and de-duped by id in useGroupMembers.
const ACCESS_GRANTED_GROUPS = [
  ENTRA_GROUP_ALL_FEE_EARNERS,
  ENTRA_GROUP_LEGAL_SUPPORT,
  ENTRA_GROUP_BUSINESS_SVCS,
];

// Session-scoped cache. Group membership doesn't change often; fetching
// on every picker mount is wasteful. Cleared when the tab closes.
const _entraGroupCache = new Map();
async function fetchGroupMembers(groupId) {
  if (_entraGroupCache.has(groupId)) return _entraGroupCache.get(groupId);
  const token = await window.IT_AUTH.getToken();
  // Paginate - Graph caps at 999 per page.
  let all = [];
  let url = `https://graph.microsoft.com/v1.0/groups/${groupId}/members?$select=id,displayName,mail,userPrincipalName,jobTitle&$top=999`;
  while (url) {
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) {
      const detail = await res.text().catch(() => '');
      throw new Error(`Graph /groups/${groupId}/members → ${res.status}${detail ? ': ' + detail.slice(0, 200) : ''}`);
    }
    const data = await res.json();
    all = all.concat(data.value || []);
    url = data['@odata.nextLink'] || null;
  }
  all.sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
  _entraGroupCache.set(groupId, all);
  return all;
}

// Shared hook: loads members, exposes a filtered-suggestions memo. Accepts a
// single group id (string) OR several (array) - members are merged and
// de-duped by id, so the "Access granted to" picker can span fee earners +
// legal support. Resilient: if one group fails to load, the others still
// show (a single bad/permissioned group won't break the whole picker).
function useGroupMembers(groups, query) {
  const [members, setMembers] = useState(null); // null = loading, [] = empty/error
  const [error, setError]     = useState(null);
  // Stable string key so the effect re-runs only when the set of groups
  // actually changes, not on every render (arrays are new identities).
  const key = (Array.isArray(groups) ? groups : [groups]).filter(Boolean).join(',');
  useEffect(() => {
    const ids = key.split(',').filter(Boolean);
    let cancelled = false;
    setError(null);
    setMembers(null);
    Promise.allSettled(ids.map(fetchGroupMembers))
      .then(results => {
        if (cancelled) return;
        const byId = new Map();
        let lastErr = null;
        for (const res of results) {
          if (res.status === 'fulfilled') {
            for (const m of res.value) if (m && m.id && !byId.has(m.id)) byId.set(m.id, m);
          } else {
            lastErr = res.reason;
            console.warn('[requests] group members load failed:', res.reason?.message || res.reason);
          }
        }
        const merged = [...byId.values()].sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
        setMembers(merged);
        // Only surface an error if we got nobody at all; a partial failure
        // degrades quietly rather than blocking the form.
        if (!merged.length && lastErr) setError(lastErr.message || 'Failed to load');
      });
    return () => { cancelled = true; };
  }, [key]);
  const suggestions = useMemo(() => {
    if (!members) return [];
    const q = String(query || '').trim().toLowerCase();
    // No slice - dropdown has max-height + overflow-y:auto so the user
    // can scroll through the full list. Capping made small groups feel
    // fine but the fee-earners group is >200 people and hiding people
    // behind an arbitrary "type something to see them" was confusing.
    if (!q) return members;
    return members.filter(m =>
      (m.displayName || '').toLowerCase().includes(q) ||
      (m.mail || m.userPrincipalName || '').toLowerCase().includes(q)
    );
  }, [members, query]);
  return { members, suggestions, error };
}

// Shape a Graph user object into what the form / backend expects.
const shapeGraphUser = (m) => ({
  id:          m.id,
  email:       (m.mail || m.userPrincipalName || '').toLowerCase(),
  displayName: m.displayName,
});

// ── EntraSinglePicker ─────────────────────────────────────────────────────
function EntraSinglePicker({ groupId, label, help, value, onChange, placeholder }) {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const { members, suggestions, error } = useGroupMembers(groupId, query);
  const ref = React.useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const pick = (m) => {
    onChange(shapeGraphUser(m));
    setQuery('');
    setOpen(false);
  };
  const clear = () => { onChange(null); setQuery(''); };

  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      <div ref={ref} style={{ position: 'relative', width: '100%' }}>
        {value ? (
          <div style={{
            ...styles.input,
            width: '100%', boxSizing: 'border-box',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: '#fff', cursor: 'default',
          }}>
            <span>{value.displayName || value.email}</span>
            <button type="button" onClick={clear} aria-label="Clear"
              style={{ background: 'none', border: 'none', color: '#6B7280', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}>✕</button>
          </div>
        ) : (
          <input
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={members === null ? 'Loading…' : (placeholder || 'Type a name')}
            style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }}
            disabled={members === null && !error}
          />
        )}
        {open && !value && suggestions.length > 0 && (
          <SuggestionList suggestions={suggestions} onPick={pick}/>
        )}
      </div>
      {error && <div style={{ ...styles.help, color: '#DC2626' }}>{error}</div>}
      {help && <div style={styles.help}>{help}</div>}
    </div>
  );
}

// ── EntraMultiPicker ──────────────────────────────────────────────────────
function EntraMultiPicker({ groupId, label, help, values, onChange, placeholder }) {
  const [query, setQuery] = useState('');
  const [open, setOpen]   = useState(false);
  const { members, suggestions, error } = useGroupMembers(groupId, query);
  const ref = React.useRef(null);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Filter out already-selected users from suggestions so we don't
  // show duplicates in the dropdown.
  const selectedIds = useMemo(() => new Set(values.map(v => v.id || v.email)), [values]);
  const remaining = suggestions.filter(m => !selectedIds.has(m.id) && !selectedIds.has((m.mail || m.userPrincipalName || '').toLowerCase()));

  const pick = (m) => {
    onChange([...values, shapeGraphUser(m)]);
    setQuery('');
  };
  const remove = (i) => onChange(values.filter((_, j) => j !== i));

  return (
    <div style={styles.field}>
      <label style={styles.label}>{label}</label>
      <div ref={ref} style={{ position: 'relative' }}>
        <div style={styles.pickerChips}>
          {values.map((v, i) => (
            <span key={v.id || v.email} style={styles.chip}>
              {v.displayName || v.email}
              <button type="button" aria-label={`Remove ${v.displayName || v.email}`}
                onClick={() => remove(i)} style={styles.chipX}>×</button>
            </span>
          ))}
          <input
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={e => {
              if (e.key === 'Backspace' && !query && values.length) remove(values.length - 1);
            }}
            placeholder={values.length ? '' : (members === null ? 'Loading…' : (placeholder || 'Type a name'))}
            style={styles.pickerInput}
            disabled={members === null && !error}
          />
        </div>
        {open && remaining.length > 0 && (
          <SuggestionList suggestions={remaining} onPick={pick}/>
        )}
      </div>
      {error && <div style={{ ...styles.help, color: '#DC2626' }}>{error}</div>}
      {help && <div style={styles.help}>{help}</div>}
    </div>
  );
}

// Shared dropdown UI for both pickers.
function SuggestionList({ suggestions, onPick }) {
  return (
    <div style={styles.suggestionList}>
      {suggestions.map(m => (
        <div key={m.id} style={styles.suggestionItem}
             onClick={() => onPick(m)}>
          <div style={{ fontSize: 13, fontWeight: 500 }}>{m.displayName}</div>
          <div style={{ fontSize: 11, color: '#6B7280' }}>
            {m.mail || m.userPrincipalName}
            {m.jobTitle ? ` · ${m.jobTitle}` : ''}
          </div>
        </div>
      ))}
    </div>
  );
}

// Wraps the POST call + error surfacing so File Access and Info Barrier
// don't each repeat the same submit boilerplate.
const usePostRequest = (onSubmitted) => {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const submit = async (payload) => {
    if (submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await reqApi('POST', '/requests', payload);
      onSubmitted();
    } catch (e) {
      // Surface the backend error message directly - validation feedback
      // for the user (e.g. "Client and matter numbers required").
      setError(e.message || 'Submit failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
  return { submit, submitting, error };
};

// ── File Access form ───────────────────────────────────────────────────────
// Inline note under the client/matter fields telling the requester whether
// a barrier policy exists for the matter. File Access only adds to an
// existing barrier, so "no barrier" is the useful warning. Neutral,
// non-blocking - the request can still be submitted (IT confirms).
function PolicyCheckNote({ check, clientNumber, matterNumber }) {
  if (!check) return null;
  const cm = `${clientNumber}.${matterNumber}`;
  if (check === 'checking') {
    return <div style={styles.checkMuted}>Checking NetDocuments for {cm}…</div>;
  }
  if (!check.checked) return null; // couldn't check (ND unavailable) - stay quiet
  if (check.exists) {
    return <div style={styles.checkOk}>✓ A file access policy exists for {cm} - your request will add access to it.</div>;
  }
  return (
    <div style={styles.checkWarn}>
      ⚠ No file access policy found for {cm} in NetDocuments. File Access adds people to an
      existing policy, so there may be nothing to add access to yet - if the matter isn't
      restricted you may already have access, or IT may need to set one up. You can still
      submit and IT will check.
    </div>
  );
}

// Inline note for the Information Barrier form. The checks are the INVERSE of
// File Access: creating a barrier needs the matter to exist and NO policy to
// be in place yet. Non-blocking - the request can still be submitted.
function InfoBarrierCheckNote({ check, clientNumber, matterNumber }) {
  if (!check) return null;
  const cm = `${clientNumber}.${matterNumber}`;
  if (check === 'checking') {
    return <div style={styles.checkMuted}>Checking NetDocuments for {cm}…</div>;
  }
  if (!check.checked) return null; // couldn't check (ND unavailable) - stay quiet
  if (check.matterExists === false) {
    return (
      <div style={styles.checkWarn}>
        ⚠ No matter {cm} found in NetDocuments - double-check the client and matter numbers.
        You can still submit and IT will check.
      </div>
    );
  }
  if (check.exists) {
    return (
      <div style={styles.checkWarn}>
        ⚠ A barrier is already in place for {cm}, so a new one isn't needed. If someone needs
        access to the existing barrier, submit a File Access request instead. You can still
        submit and IT will confirm.
      </div>
    );
  }
  return <div style={styles.checkOk}>✓ {cm} exists and has no barrier yet - your request will create one.</div>;
}

function FileAccessForm({ onSubmitted }) {
  const [clientNumber,  setClientNumber]  = useState('');
  const [matterNumber,  setMatterNumber]  = useState('');
  const [matterPartner, setMatterPartner] = useState(null);
  const [reason,        setReason]        = useState('');
  const [accessGrantedTo, setAccessGrantedTo] = useState([]);
  const { submit, submitting, error } = usePostRequest(onSubmitted);

  // Live "does a barrier exist for this matter?" check. File Access only
  // adds users to an EXISTING barrier, so a matter with no policy is a dead
  // end - warn the requester before they submit. Debounced; fires only when
  // both fields are complete. Returns only a boolean (see backend note).
  const [policyCheck, setPolicyCheck] = useState(null); // null | 'checking' | {checked, exists}
  useEffect(() => {
    if (!/^\d{5}$/.test(clientNumber) || !/^\d{3}$/.test(matterNumber)) { setPolicyCheck(null); return; }
    let cancelled = false;
    setPolicyCheck('checking');
    const t = setTimeout(async () => {
      try {
        const r = await reqApi('GET', `/requests/policy-check?client=${clientNumber}&matter=${matterNumber}`);
        if (!cancelled) setPolicyCheck(r || { checked: false });
      } catch (_) {
        if (!cancelled) setPolicyCheck({ checked: false });
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [clientNumber, matterNumber]);

  const canSubmit = useMemo(() => (
    /^\d{5}$/.test(clientNumber) &&
    /^\d{3}$/.test(matterNumber) &&
    !!matterPartner &&
    reason.trim().length >= 5 &&
    accessGrantedTo.length > 0
  ), [clientNumber, matterNumber, matterPartner, reason, accessGrantedTo]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    submit({
      type: 'file-access',
      clientNumber, matterNumber, matterPartner,
      reason: reason.trim(),
      accessGrantedTo,
    });
  };

  return (
    <form onSubmit={onSubmit} style={styles.form}>
      <div style={styles.row2}>
        <TextField label="Client number" value={clientNumber} onChange={setClientNumber} autoNumeric maxLength={5} placeholder="12345" help="The client's 5-digit number in NetDocuments."/>
        <TextField label="Matter number" value={matterNumber} onChange={setMatterNumber} autoNumeric maxLength={3} placeholder="001" help="The 3-digit matter number under that client."/>
      </div>
      <PolicyCheckNote check={policyCheck} clientNumber={clientNumber} matterNumber={matterNumber}/>
      <EntraSinglePicker
        groupId={ENTRA_GROUP_ALL_PARTNERS}
        label="Matter partner"
        value={matterPartner} onChange={setMatterPartner}
        placeholder="Type to search partners…"
        help="We assume you've already checked with them that access is OK - if not, please do that first."/>
      <TextArea label="Reason for access" value={reason} onChange={setReason} placeholder="Briefly explain why you need access." help="Reviewed by IT before approval."/>
      <EntraMultiPicker
        groupId={ACCESS_GRANTED_GROUPS}
        label="Access granted to"
        values={accessGrantedTo} onChange={setAccessGrantedTo}
        placeholder="Type to search fee earners, legal support, business services…"
        help="Grant access to anyone who needs it - the matter partner above is copied into the approval email for reference."/>
      <SubmitBar canSubmit={canSubmit} submitting={submitting} error={error}/>
    </form>
  );
}

// ── Information Barrier form ───────────────────────────────────────────────
function InfoBarrierForm({ onSubmitted }) {
  const [clientNumber,  setClientNumber]  = useState('');
  const [matterNumber,  setMatterNumber]  = useState('');
  const [matterPartner, setMatterPartner] = useState(null);
  const [accessGrantedTo, setAccessGrantedTo] = useState([]);
  const { submit, submitting, error } = usePostRequest(onSubmitted);

  // Live "does the matter exist / is a barrier already in place?" check.
  // Info Barrier creates a NEW barrier, so both matter existence and the
  // ABSENCE of an existing policy matter. ?type=info-barrier opts the
  // endpoint into the extra matter lookup. Debounced; booleans only.
  const [policyCheck, setPolicyCheck] = useState(null); // null | 'checking' | {checked, exists, matterExists}
  useEffect(() => {
    if (!/^\d{5}$/.test(clientNumber) || !/^\d{3}$/.test(matterNumber)) { setPolicyCheck(null); return; }
    let cancelled = false;
    setPolicyCheck('checking');
    const t = setTimeout(async () => {
      try {
        const r = await reqApi('GET', `/requests/policy-check?client=${clientNumber}&matter=${matterNumber}&type=info-barrier`);
        if (!cancelled) setPolicyCheck(r || { checked: false });
      } catch (_) {
        if (!cancelled) setPolicyCheck({ checked: false });
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(t); };
  }, [clientNumber, matterNumber]);

  const canSubmit = useMemo(() => (
    /^\d{5}$/.test(clientNumber) &&
    /^\d{3}$/.test(matterNumber) &&
    !!matterPartner &&
    accessGrantedTo.length > 0
  ), [clientNumber, matterNumber, matterPartner, accessGrantedTo]);

  const onSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    submit({
      type: 'info-barrier',
      clientNumber, matterNumber, matterPartner,
      accessGrantedTo,
    });
  };

  return (
    <form onSubmit={onSubmit} style={styles.form}>
      <div style={styles.row2}>
        <TextField label="Client number" value={clientNumber} onChange={setClientNumber} autoNumeric maxLength={5} placeholder="12345" help="The client's 5-digit number in NetDocuments."/>
        <TextField label="Matter number" value={matterNumber} onChange={setMatterNumber} autoNumeric maxLength={3} placeholder="001" help="The 3-digit matter number under that client."/>
      </div>
      <InfoBarrierCheckNote check={policyCheck} clientNumber={clientNumber} matterNumber={matterNumber}/>
      <EntraSinglePicker
        groupId={ENTRA_GROUP_ALL_PARTNERS}
        label="Matter partner"
        value={matterPartner} onChange={setMatterPartner}
        placeholder="Type to search partners…"/>
      <EntraMultiPicker
        groupId={ACCESS_GRANTED_GROUPS}
        label="Access granted to"
        values={accessGrantedTo} onChange={setAccessGrantedTo}
        placeholder="Type to search fee earners, legal support, business services…"
        help="You and the matter partner are NOT automatically included - add anyone (including yourself or the partner) who needs access."/>
      <SubmitBar canSubmit={canSubmit} submitting={submitting} error={error}/>
    </form>
  );
}

function SubmitBar({ canSubmit, submitting, error }) {
  return (
    <div>
      <div style={styles.submitRow}>
        <button
          type="submit"
          disabled={!canSubmit || submitting}
          style={{ ...styles.submit, ...(!canSubmit || submitting ? styles.submitDisabled : {}) }}
        >
          {submitting ? 'Submitting…' : 'Submit request'}
        </button>
      </div>
      {error && (
        <div style={{
          marginTop: 8, fontSize: 13, color: '#DC2626',
          padding: '8px 12px', background: '#FEF2F2',
          border: '1px solid #FCA5A5', borderRadius: 6,
        }}>
          {error}
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────
// Inline - see file header. Two page wrappers (chromeless vs embedded).

// Tokens match the main Triage app (public/styles.css :root) so the portal
// reads as part of the same product rather than a generic form template:
// Inter (not system-ui), a WARM near-white ground (not cool bluish grey),
// and the app's 6px radius (not the softer 10px). This is the core of the
// "less AI-looking" pass - templates default to system-ui + cool-grey +
// big rounding; matching the real product's identity is what grounds it.
const T = {
  bg:        '#F6F5F3',   // warm light neutral (app bg is warm off-white)
  card:      '#FFFFFF',
  border:    '#E7E5E2',   // warm-tinted border to match
  text:      '#1F2937',
  muted:     '#6B7280',
  faint:     '#9CA3AF',
  accent:    '#0D9488',
  accentDk:  '#0F766E',
  accentBg:  '#ECFDF9',
  radius:    6,
  font:      '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
};

const styles = {
  page: {
    // Global `html, body { overflow: hidden }` in styles.css blocks
    // window-level scrolling (needed by the admin shell). The chromeless
    // portal owns the viewport instead, so make this div the scroll
    // container - fixed viewport height + overflow-y auto.
    height: '100vh',
    overflowY: 'auto',
    background: T.bg,
    fontFamily: T.font,
    color: T.text,
    padding: '32px 16px 64px',
    boxSizing: 'border-box',
  },
  pageEmbedded: {
    // Inside the admin app - inherit the shell's background and don't
    // force a full-viewport wrap. Padding still gives the card breathing
    // room from the topbar.
    fontFamily: T.font,
    color: T.text,
    padding: '24px 24px 48px',
    boxSizing: 'border-box',
  },
  card: {
    maxWidth: 720,
    margin: '0 auto',
    background: T.card,
    border: `1px solid ${T.border}`,
    borderRadius: T.radius,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    padding: '24px 32px 32px',
  },
  brandRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 24, paddingBottom: 16, borderBottom: `1px solid ${T.border}`,
  },
  brand: { display: 'flex', alignItems: 'center', gap: 10 },
  brandMark: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 32, height: 32, borderRadius: 6,
    background: T.accent, color: '#fff', fontWeight: 700, fontSize: 16,
  },
  brandText: { fontWeight: 700, fontSize: 16, letterSpacing: 0.5 },
  userChip: {
    display: 'flex', alignItems: 'center', gap: 10,
    fontSize: 13, color: T.muted,
  },
  userName: { fontWeight: 500 },
  signOutBtn: {
    background: 'transparent', border: `1px solid ${T.border}`,
    padding: '4px 10px', borderRadius: 4, fontSize: 12, color: T.muted,
    cursor: 'pointer',
  },
  backLink: {
    background: 'transparent', border: `1px solid ${T.border}`,
    padding: '5px 12px', borderRadius: 6, fontSize: 13, color: T.muted,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  section: { display: 'flex', flexDirection: 'column', gap: 16 },
  h1: { fontSize: 21, fontWeight: 650, margin: 0, letterSpacing: '-0.01em' },
  subtle: { fontSize: 13, color: T.muted, margin: 0, lineHeight: 1.5, textWrap: 'balance' },
  tileGrid: {
    display: 'grid', gap: 12, marginTop: 8,
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
  },
  tile: {
    display: 'block', textAlign: 'left', cursor: 'pointer',
    padding: '18px 18px 20px', borderRadius: T.radius,
    border: `1px solid ${T.border}`, background: '#fff',
    transition: 'border-color 120ms, box-shadow 120ms',
    fontFamily: 'inherit', color: T.text,
    // Uniform height so tiles match across rows (a lone tile in its own row
    // would otherwise be shorter). Sized to the existing two-line tiles +
    // box-sizing so it doesn't grow them.
    minHeight: 152, boxSizing: 'border-box',
  },
  // Tinted rounded square holding the line icon - the same visual language
  // as the app's category chips, and a world away from a bare 28px emoji.
  tileIcon: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 38, height: 38, borderRadius: T.radius,
    background: T.accentBg, color: T.accentDk, marginBottom: 14,
  },
  tileTitle: { fontSize: 15, fontWeight: 600, marginBottom: 4 },
  tileBlurb: { fontSize: 12, color: T.muted, lineHeight: 1.5 },
  recentBlock: { marginTop: 24, paddingTop: 20, borderTop: `1px solid ${T.border}` },
  recentHeader: { fontSize: 13, fontWeight: 600, color: T.text, marginBottom: 8 },
  recentToggle: {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
    background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
    fontSize: 13, fontWeight: 600, color: T.text, fontFamily: 'inherit', textAlign: 'left',
  },
  recentCount: {
    fontSize: 11, fontWeight: 600, color: T.muted, background: T.bg,
    borderRadius: 999, padding: '1px 8px', marginLeft: 2,
  },
  recentEmpty: {
    fontSize: 12, color: T.faint, fontStyle: 'italic', padding: '12px 0',
  },
  successIcon: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 48, height: 48, borderRadius: '50%',
    background: T.accentBg, color: T.accent,
    fontSize: 24, fontWeight: 700, marginBottom: 4,
  },
  formHeader: {
    display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20,
  },
  backBtn: {
    background: 'transparent', border: `1px solid ${T.border}`,
    padding: '5px 12px', borderRadius: 6, fontSize: 13, color: T.muted,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  formType: { fontSize: 16, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 10 },
  formTypeIcon: {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 30, height: 30, borderRadius: T.radius,
    background: T.accentBg, color: T.accentDk, flexShrink: 0,
  },
  formHelp: { fontSize: 13, color: T.muted, padding: 24, textAlign: 'center' },
  form: { display: 'flex', flexDirection: 'column', gap: 18 },
  row2: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  field: { display: 'flex', flexDirection: 'column', gap: 6 },
  label: { fontSize: 13, fontWeight: 600, color: T.text },
  input: {
    padding: '9px 12px', border: `1px solid ${T.border}`, borderRadius: 6,
    fontSize: 14, color: T.text, background: '#fff', outline: 'none',
    fontFamily: 'inherit',
  },
  textarea: {
    padding: '9px 12px', border: `1px solid ${T.border}`, borderRadius: 6,
    fontSize: 14, color: T.text, background: '#fff', outline: 'none',
    fontFamily: 'inherit', resize: 'vertical', minHeight: 72,
  },
  help: { fontSize: 12, color: T.faint, lineHeight: 1.4 },
  checkMuted: { fontSize: 12, color: T.muted, marginTop: -8 },
  checkOk: { fontSize: 12, color: T.accentDk, marginTop: -8, fontWeight: 500 },
  checkWarn: {
    fontSize: 12, color: '#92400E', lineHeight: 1.45, marginTop: -8,
    padding: '9px 12px', background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: T.radius,
  },
  pickerChips: {
    display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center',
    padding: '6px 8px', border: `1px solid ${T.border}`, borderRadius: 6,
    background: '#fff', minHeight: 40,
  },
  chip: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '3px 4px 3px 10px', borderRadius: 4,
    background: T.accentBg, color: T.accentDk,
    fontSize: 13, fontWeight: 500,
  },
  chipX: {
    background: 'transparent', border: 'none', color: T.accentDk,
    cursor: 'pointer', padding: '0 6px', fontSize: 16, lineHeight: 1,
  },
  pickerInput: {
    border: 'none', outline: 'none', flex: 1, minWidth: 160,
    fontSize: 14, padding: '4px 6px', fontFamily: 'inherit',
    color: T.text, background: 'transparent',
  },
  suggestionList: {
    position: 'absolute', top: '100%', left: 0, right: 0,
    marginTop: 4, background: '#fff', border: `1px solid ${T.border}`,
    borderRadius: 6, boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    maxHeight: 280, overflowY: 'auto', zIndex: 20,
  },
  suggestionItem: {
    padding: '8px 12px', cursor: 'pointer',
    borderBottom: `1px solid ${T.border}`,
    transition: 'background 80ms',
  },
  submitRow: {
    display: 'flex', alignItems: 'center', gap: 16,
    marginTop: 8, flexWrap: 'wrap',
  },
  submit: {
    padding: '10px 20px', background: T.accent, color: '#fff',
    border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600,
    cursor: 'pointer', transition: 'background 100ms',
    fontFamily: 'inherit',
  },
  submitDisabled: {
    background: T.faint, cursor: 'not-allowed',
  },
  mineList: { display: 'flex', flexDirection: 'column', gap: 6 },
  mineRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    padding: '10px 12px', border: `1px solid ${T.border}`, borderRadius: 6,
    background: '#fff',
  },
  mineRowMain: { minWidth: 0, flex: 1 },
  mineRowTitle: { fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 2 },
  mineRowCM: { color: T.muted, fontWeight: 400 },
  mineRowMeta: { fontSize: 11, color: T.faint },
  pill: {
    display: 'inline-block', padding: '2px 8px', borderRadius: 10,
    fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
  },
  filterBtn: {
    padding: '4px 12px', fontSize: 12, borderRadius: 4, cursor: 'pointer',
    background: '#fff', color: T.muted, border: `1px solid ${T.border}`,
    fontFamily: 'inherit',
  },
  filterBtnActive: {
    background: T.accentBg, color: T.accentDk, borderColor: T.accent,
  },
  adminRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 20px', cursor: 'pointer', gap: 12,
    background: '#fff',
  },
  detailLbl: {
    padding: '4px 16px 4px 0', color: T.muted, fontWeight: 500,
    verticalAlign: 'top', whiteSpace: 'nowrap',
  },
  detailVal: { padding: '4px 0', color: T.text, verticalAlign: 'top' },
  approveBtn: {
    padding: '6px 14px', background: T.accent, color: '#fff',
    border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  ndCard: {
    background: '#fff', border: `1px solid ${T.border}`, borderRadius: 8,
    padding: '16px 20px', marginBottom: 24,
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16,
    flexWrap: 'wrap',
  },
  ndConnectBtn: {
    padding: '8px 16px', background: T.accent, color: '#fff',
    border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  rejectBtn: {
    padding: '6px 14px', background: '#DC2626', color: '#fff',
    border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  cancelBtn: {
    padding: '6px 14px', background: '#fff', color: T.muted,
    border: `1px solid ${T.border}`, borderRadius: 4, fontSize: 13,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  deleteLink: {
    padding: '6px 12px', background: 'transparent',
    border: 'none', borderRadius: 4, fontSize: 12,
    color: '#DC2626', cursor: 'pointer', fontFamily: 'inherit',
    textDecoration: 'underline',
  },
};

// ── Admin User Requests list view ──────────────────────────────────────────
// Rendered by app.jsx when state.view === 'user-requests'. IT-only -
// gated by the standard sidebar-nav path (only IT users see the app
// shell). Reads GET /api/requests and lists every submission with
// Approve / Reject buttons on pending rows. Slice A: decisions just
// mark status in SQL + email the submitter. Slice B: wires approve to
// actually call NetDocuments.
export function UserRequestsAdmin() {
  const [requests, setRequests] = useState(null); // null = loading
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('pending'); // 'pending' | 'all'
  // Which row is expanded for detail. null = none. Number = request id.
  const [openId, setOpenId] = useState(null);
  // Optional reject-with-note UI state: when non-null, showing the note
  // input for that request id.
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectNote, setRejectNote] = useState('');

  const [refreshing, setRefreshing] = useState(false);
  const load = useCallback(async () => {
    setError(null);
    setRefreshing(true);
    try { setRequests(await reqApi('GET', '/requests') || []); }
    catch (e) { setError(e.message || 'Failed to load'); setRequests([]); }
    finally { setRefreshing(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Auto-refresh whenever the tab regains focus - cheap way to keep the
  // queue current when a colleague submits from the portal (or when the
  // admin has both /requests and /user-requests open in separate tabs).
  // Only fires when the tab becomes visible again, so it's not chatty.
  useEffect(() => {
    const onVis = () => { if (document.visibilityState === 'visible') load(); };
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [load]);

  const decide = async (id, decision, note) => {
    try {
      await reqApi('PATCH', `/requests/${id}`, { decision, note: note || null });
      // Local optimistic update - flip status, then refetch to get the
      // authoritative reviewedBy + reviewedAt from the server.
      setRequests(rs => (rs || []).map(r => r.id === id ? { ...r, status: decision } : r));
      setRejectingId(null); setRejectNote('');
      load();
    } catch (e) {
      alert(`Decision failed: ${e.message}`);
    }
  };

  const deleteRequest = async (id) => {
    // Belt-and-braces confirm. The row is hard-deleted server-side;
    // any WSM change in NetDocuments is NOT undone (cleanup there is
    // separate).
    const r = (requests || []).find(x => x.id === id);
    const label = r ? `${REQUEST_TYPES.find(t => t.id === r.type)?.label || r.type} ${r.clientNumber}.${r.matterNumber}` : `request ${id}`;
    if (!window.confirm(`Delete ${label}?\n\nThis removes the Triage record only. Any NetDocuments policy already applied stays in place - you'd need to remove that separately in NetDocuments.`)) return;
    try {
      await reqApi('DELETE', `/requests/${id}`);
      setRequests(rs => (rs || []).filter(x => x.id !== id));
    } catch (e) {
      alert(`Delete failed: ${e.message}`);
    }
  };

  const visible = useMemo(() => {
    if (!Array.isArray(requests)) return [];
    if (filter === 'pending') return requests.filter(r => r.status === 'pending');
    return requests;
  }, [requests, filter]);

  const pendingCount = Array.isArray(requests) ? requests.filter(r => r.status === 'pending').length : 0;

  return (
    <div style={{ padding: '24px 32px', flex: 1, minHeight: 0, overflowY: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 16, marginBottom: 6 }}>
        <h1 style={{ fontSize: 20, fontWeight: 600, margin: 0 }}>User Requests</h1>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button
            type="button"
            onClick={load}
            disabled={refreshing}
            title="Refresh"
            style={{ ...styles.filterBtn, opacity: refreshing ? 0.6 : 1 }}
          >{refreshing ? '…' : '↻'} Refresh</button>
          <button
            type="button"
            onClick={() => setFilter('pending')}
            style={{ ...styles.filterBtn, ...(filter === 'pending' ? styles.filterBtnActive : {}) }}
          >Pending ({pendingCount})</button>
          <button
            type="button"
            onClick={() => setFilter('all')}
            style={{ ...styles.filterBtn, ...(filter === 'all' ? styles.filterBtnActive : {}) }}
          >All ({Array.isArray(requests) ? requests.length : 0})</button>
        </div>
      </div>
      <p style={{ fontSize: 13, color: T.muted, margin: '0 0 12px' }}>
        Requests submitted through the user-facing portal.
      </p>
      <div style={{ marginBottom: 24 }}>
        <a
          href="/requests"
          target="_blank"
          rel="noopener"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', background: '#fff', color: T.accent,
            border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 13,
            fontWeight: 600, textDecoration: 'none', cursor: 'pointer',
          }}
        >Open user portal ↗</a>
      </div>

      {error && (
        <div style={{
          padding: '10px 14px', background: '#FEF2F2', color: '#991B1B',
          border: '1px solid #FCA5A5', borderRadius: 6, marginBottom: 16, fontSize: 13,
        }}>
          Failed to load requests: {error}
        </div>
      )}

      {requests === null ? (
        <div style={{ padding: '48px 24px', textAlign: 'center', color: T.faint }}>Loading requests…</div>
      ) : visible.length === 0 ? (
        <div style={{
          border: `1px solid ${T.border}`, borderRadius: 8,
          background: '#fff', padding: '48px 24px', textAlign: 'center', color: T.faint,
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>📥</div>
          <div style={{ fontSize: 14, color: T.muted, marginBottom: 4 }}>
            {filter === 'pending' ? 'No pending requests.' : 'No requests yet.'}
          </div>
        </div>
      ) : (
        <div style={{
          border: `1px solid ${T.border}`, borderRadius: 8,
          background: '#fff', overflow: 'hidden',
        }}>
          {visible.map(r => (
            <AdminRow
              key={r.id}
              r={r}
              open={openId === r.id}
              onToggle={() => setOpenId(id => id === r.id ? null : r.id)}
              rejecting={rejectingId === r.id}
              rejectNote={rejectNote}
              setRejectNote={setRejectNote}
              onStartReject={() => { setRejectingId(r.id); setRejectNote(''); }}
              onCancelReject={() => { setRejectingId(null); setRejectNote(''); }}
              onApprove={() => decide(r.id, 'approved')}
              onConfirmReject={() => decide(r.id, 'rejected', rejectNote)}
              onDelete={() => deleteRequest(r.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// Handles the OAuth callback from NetDocuments. NetDocuments redirects
// the user's browser to /oauth-callback?code=X&state=Y. This component
// reads those params, POSTs them to the backend with the IT user's
// Bearer token to complete the token exchange, then shows success or
// error. Kept chromeless (own layout, no sidebar) because it's a
// mid-flow OAuth landing page, not a normal app view.
//
// Why POST via authenticated frontend rather than a public GET on the
// backend: Easy Auth on the Function App requires a valid token on
// every request. A public callback would need us to weaken that gate,
// which we're deliberately not doing (security is paramount).
export function NdOAuthCallback() {
  const [status, setStatus] = useState('working'); // 'working' | 'ok' | 'error'
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const code    = params.get('code');
    const state   = params.get('state');
    const ndError = params.get('error');
    const ndErrorDesc = params.get('error_description');

    // NetDocuments returned an error (e.g. user clicked Deny). Show it
    // without ever calling the backend - no state to burn.
    if (ndError) {
      setStatus('error');
      setDetail(`NetDocuments returned an error: ${ndError}${ndErrorDesc ? ` - ${ndErrorDesc}` : ''}`);
      return;
    }
    if (!code || !state) {
      setStatus('error');
      setDetail('Missing code or state in the callback URL - the OAuth flow didn\'t complete.');
      return;
    }

    (async () => {
      try {
        const result = await reqApi('POST', '/nd/oauth-callback', { code, state });
        setStatus('ok');
        setDetail(result);
      } catch (e) {
        setStatus('error');
        setDetail(e.message || 'Token exchange failed.');
      }
    })();
  }, []);

  return (
    <div style={styles.page}>
      <div style={{ ...styles.card, textAlign: 'center' }}>
        {status === 'working' && (
          <>
            <div style={{ fontSize: 32, marginBottom: 12 }}>⏳</div>
            <h1 style={styles.h1}>Connecting to NetDocuments…</h1>
            <p style={styles.subtle}>Exchanging the authorization code. This takes a couple of seconds.</p>
          </>
        )}
        {status === 'ok' && (
          <>
            <div style={styles.successIcon}>✓</div>
            <h1 style={styles.h1}>Connected to NetDocuments</h1>
            <p style={styles.subtle}>
              Authorized by <strong>{detail?.connectedBy || '-'}</strong>.<br/>
              Access token expires at {detail?.accessTokenExpiresAt || '-'}.<br/>
              {detail?.ndRefreshTokenExpiresIn
                ? <>Refresh token lifetime: {Math.round(detail.ndRefreshTokenExpiresIn / 86400)} days.</>
                : <>Refresh token lifetime: not disclosed by NetDocuments (usually long-lived).</>}
            </p>
            <p style={{ ...styles.subtle, marginTop: 16 }}>
              No NetDocuments data has been read or written - this proves only that the credential exchange works.
            </p>
            <div style={{ marginTop: 24 }}>
              <a href="/user-requests" style={{ ...styles.submit, textDecoration: 'none', display: 'inline-block' }}>Back to User Requests</a>
            </div>
          </>
        )}
        {status === 'error' && (
          <>
            <div style={{ fontSize: 32, marginBottom: 12, color: '#DC2626' }}>✕</div>
            <h1 style={{ ...styles.h1, color: '#DC2626' }}>Connection failed</h1>
            <div style={{
              padding: '12px 16px', background: '#FEF2F2', color: '#991B1B',
              border: '1px solid #FCA5A5', borderRadius: 6, marginTop: 16,
              fontSize: 13, textAlign: 'left',
            }}>{typeof detail === 'string' ? detail : JSON.stringify(detail)}</div>
            <div style={{ marginTop: 24 }}>
              <a href="/user-requests" style={{ ...styles.submit, textDecoration: 'none', display: 'inline-block' }}>Back to Triage</a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// NetDocuments connection card. Rendered on Settings > Integrations
// (was originally on User Requests admin, moved because it's an admin-
// config concern, not a "triage incoming requests" concern). Also
// exported so external screens can embed it if needed. Shows whether
// Triage is currently connected to NetDocuments (has a live refresh
// token), exposes a Connect / Reconnect button that kicks off the
// OAuth dance, and a read-only Test button to prove the stored
// credentials still work end-to-end.
//
// The Connect button calls GET /api/nd/oauth-start via the IT Bearer
// token (works because the frontend has the token). The response
// includes an authorizationUrl - we navigate the whole tab to it and
// let NetDocuments handle sign-in + Allow. NetDocuments then redirects
// to /oauth-callback (Static Web App route) which completes the
// exchange via the frontend.
export function NdConnectionCard() {
  const [status, setStatus] = useState(null); // null=loading, {connected: bool, ...}
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [readsBusy, setReadsBusy] = useState(false);
  const [readsResult, setReadsResult] = useState(null);
  const [readsClient, setReadsClient] = useState('');
  const [readsMatter, setReadsMatter] = useState('');

  const load = useCallback(async () => {
    try { setStatus(await reqApi('GET', '/nd/status')); }
    catch (e) { setError(e.message); setStatus({ connected: false }); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      const { authorizationUrl } = await reqApi('GET', '/nd/oauth-start');
      window.location.href = authorizationUrl;
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  // Read-only test: pull repository users from ND. Proves the stored
  // refresh token works, auto-refresh kicks in when needed, and we
  // can actually talk to NetDocuments APIs. Zero risk to any ND data.
  const testConnection = async () => {
    if (testing) return;
    setTesting(true); setTestResult(null);
    try {
      const r = await reqApi('GET', '/nd/test-users');
      setTestResult(r);
    } catch (e) {
      setTestResult({ ok: false, error: e.message });
    } finally {
      setTesting(false);
    }
  };

  // Runs every ND read wrapper against the current cabinet. Shows a
  // per-op pass/fail table. If client+matter are filled in, also runs
  // getClientEntry / getMatterEntry - proves the workspace-existence
  // check the Info Barrier flow depends on.
  const runAllReads = async () => {
    if (readsBusy) return;
    setReadsBusy(true); setReadsResult(null);
    try {
      const qs = new URLSearchParams();
      if (readsClient.trim()) qs.set('client', readsClient.trim());
      if (readsMatter.trim()) qs.set('matter', readsMatter.trim());
      const path = qs.toString() ? `/nd/test-reads?${qs.toString()}` : '/nd/test-reads';
      setReadsResult(await reqApi('GET', path));
    } catch (e) {
      setReadsResult({ error: e.message });
    } finally {
      setReadsBusy(false);
    }
  };

  if (status === null) return null;

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={styles.ndCard}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 4 }}>
            NetDocuments connection
            {status.connected
              ? <span style={{ ...styles.pill, background: '#D1FAE5', color: '#065F46', marginLeft: 10 }}>Connected</span>
              : <span style={{ ...styles.pill, background: '#FEF3C7', color: '#92400E', marginLeft: 10 }}>Not connected</span>}
          </div>
          <div style={{ fontSize: 12, color: T.muted }}>
            {status.connected
              ? <>Authorized by {status.connectedBy || '-'} · connected {fmtDate(status.connectedAt)}</>
              : <>Slice B needs a one-time OAuth sign-in to NetDocuments before it can apply WSM changes.</>}
          </div>
          {error && (
            <div style={{ fontSize: 12, color: '#DC2626', marginTop: 6 }}>{error}</div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {status.connected && (
            <>
              <button type="button" onClick={testConnection} disabled={testing} style={{ ...styles.filterBtn, padding: '8px 14px' }}>
                {testing ? 'Testing…' : 'Test read-only'}
              </button>
              <button type="button" onClick={runAllReads} disabled={readsBusy} style={{ ...styles.filterBtn, padding: '8px 14px' }}>
                {readsBusy ? 'Running…' : 'Test all reads'}
              </button>
            </>
          )}
          <button type="button" onClick={connect} disabled={busy} style={styles.ndConnectBtn}>
            {busy ? 'Redirecting…' : status.connected ? 'Reconnect' : 'Connect to NetDocuments'}
          </button>
        </div>
      </div>
      {status.connected && (
        <div style={{ marginTop: 8, padding: '10px 14px', background: '#F9FAFB', border: `1px solid ${T.border}`, borderRadius: 6, fontSize: 12, color: T.muted }}>
          <div style={{ marginBottom: 6 }}>Optionally test a real workspace lookup:</div>
          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input type="text" placeholder="Client no. e.g. 05311" value={readsClient} onChange={e => setReadsClient(e.target.value)}
              style={{ padding: '4px 8px', border: `1px solid ${T.border}`, borderRadius: 4, fontSize: 12, width: 140 }}/>
            <input type="text" placeholder="Matter no. e.g. 001" value={readsMatter} onChange={e => setReadsMatter(e.target.value)}
              style={{ padding: '4px 8px', border: `1px solid ${T.border}`, borderRadius: 4, fontSize: 12, width: 140 }}/>
            <span style={{ color: T.faint }}>(leave blank to skip lookup ops)</span>
          </div>
        </div>
      )}
      {testResult && (
        <div style={{
          marginTop: 8, padding: '12px 16px', borderRadius: 6, fontSize: 13,
          background: testResult.ok ? '#F0FDFA' : '#FEF2F2',
          border: `1px solid ${testResult.ok ? '#5EEAD4' : '#FCA5A5'}`,
          color: testResult.ok ? '#0F766E' : '#991B1B',
        }}>
          {testResult.ok ? (
            <>
              <strong>✓ Success</strong> - got {testResult.count} users from NetDocuments (repo CA-L1SBFUED). First few:
              <ul style={{ margin: '6px 0 0 20px', padding: 0 }}>
                {(testResult.firstFew || []).map(u => (
                  <li key={u.id}>{u.displayName || u.email} <span style={{ color: T.muted }}>({u.email})</span></li>
                ))}
              </ul>
            </>
          ) : (
            <><strong>✕ Failed:</strong> {testResult.error}</>
          )}
        </div>
      )}
      {readsResult && (
        <div style={{
          marginTop: 8, padding: '12px 16px', borderRadius: 6, fontSize: 13,
          background: '#fff', border: `1px solid ${T.border}`, color: T.text,
        }}>
          {readsResult.error ? (
            <div style={{ color: '#991B1B' }}><strong>✕ Batch failed:</strong> {readsResult.error}</div>
          ) : (
            <>
              <div style={{ marginBottom: 8, color: T.muted, fontSize: 12 }}>
                Cabinet: <code style={{ background: '#F3F4F6', padding: '1px 6px', borderRadius: 3 }}>{readsResult.cabinet}</code>
              </div>
              <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: 12 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: T.muted }}>
                    <th style={{ padding: '4px 8px 4px 0' }}>Operation</th>
                    <th style={{ padding: '4px 8px' }}>Result</th>
                    <th style={{ padding: '4px 0' }}>Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {(readsResult.results || []).map(r => (
                    <tr key={r.name} style={{ borderTop: `1px solid ${T.border}`, verticalAlign: 'top' }}>
                      <td style={{ padding: '6px 8px 6px 0', fontFamily: 'monospace', fontSize: 11, whiteSpace: 'nowrap' }}>{r.name}</td>
                      <td style={{ padding: '6px 8px' }}>
                        {r.ok
                          ? <span style={{ color: '#0F766E', fontWeight: 600 }}>✓</span>
                          : <span style={{ color: '#DC2626', fontWeight: 600 }}>✕</span>}
                      </td>
                      <td style={{ padding: '6px 0', color: r.ok ? T.muted : '#991B1B', fontFamily: 'monospace', fontSize: 11, wordBreak: 'break-all' }}>
                        {r.ok
                          ? JSON.stringify(r.preview).slice(0, 200) + (JSON.stringify(r.preview).length > 200 ? '…' : '')
                          : r.error}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Renders the audit trail for a request that was actually applied to
// NetDocuments (i.e. TRIAGE_ND_ENABLED=true was on when the reviewer
// approved). Shows the policy identity, cabinet, the ACL entries that
// were already on the policy, and the entries we added. Nothing here
// is decorative - this is the "what did Triage do in NetDocuments"
// record for the audit trail.
export function WsmApplyDetail({ apply }) {
  if (!apply) return null;
  const before = Array.isArray(apply.aclBefore) ? apply.aclBefore : [];
  const added  = Array.isArray(apply.aclAdded)  ? apply.aclAdded  : [];
  const isCreated = apply.action === 'created';

  const entityIcon = (e) => e.entityType === 2 ? '👥' : '👤';
  const entryText  = (e) => `${entityIcon(e)} ${e.displayName || e.id}${e.email ? ` (${e.email})` : ''}`;

  return (
    <div style={{
      marginTop: 12, padding: '12px 16px',
      background: '#F0FDFA', border: '1px solid #5EEAD4',
      borderRadius: 6, fontSize: 12,
    }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: '#0F766E', marginBottom: 6 }}>
        {isCreated
          ? '✓ Information Barrier created in NetDocuments'
          : '✓ Applied in NetDocuments'}
      </div>
      <table style={{ borderCollapse: 'collapse' }}>
        <tbody>
          <tr>
            <td style={{ ...styles.detailLbl, color: '#0F766E' }}>Policy</td>
            <td style={styles.detailVal}>
              {apply.policyName || '-'}
              {apply.policyId && (
                <span style={{ color: T.faint, fontFamily: 'monospace', fontSize: 11, marginLeft: 6 }}>{apply.policyId}</span>
              )}
            </td>
          </tr>
          <tr>
            <td style={{ ...styles.detailLbl, color: '#0F766E' }}>Cabinet</td>
            <td style={styles.detailVal}>
              {apply.cabinetName && apply.cabinetName !== apply.cabinet
                ? <>{apply.cabinetName} <span style={{ color: T.faint, fontFamily: 'monospace', fontSize: 11, marginLeft: 6 }}>{apply.cabinet}</span></>
                : <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{apply.cabinet || '-'}</span>}
            </td>
          </tr>
          {/* File Access: show before + added. Info Barrier: policy is
              brand new so 'before' is always empty, only show 'aclAdded'
              (= final ACL state). */}
          {!isCreated && (
            <tr>
              <td style={{ ...styles.detailLbl, color: '#0F766E', verticalAlign: 'top' }}>
                Users on policy before ({before.length})
              </td>
              <td style={styles.detailVal}>
                {before.length === 0
                  ? <span style={{ color: T.faint }}>- none -</span>
                  : <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {before.map(e => <li key={e.id}>{entryText(e)}</li>)}
                    </ul>}
              </td>
            </tr>
          )}
          <tr>
            <td style={{ ...styles.detailLbl, color: '#0F766E', verticalAlign: 'top' }}>
              {isCreated ? `ACL on new policy (${added.length})` : `Users added (${added.length})`}
            </td>
            <td style={styles.detailVal}>
              {added.length === 0
                ? <span style={{ color: T.faint }}>- none -</span>
                : <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {added.map(e => <li key={e.id}><strong>{entryText(e)}</strong></li>)}
                  </ul>}
            </td>
          </tr>
          {/* Info Barrier only: workspaces applied + matter description lock */}
          {isCreated && Array.isArray(apply.workspacesApplied) && apply.workspacesApplied.length > 0 && (
            <tr>
              <td style={{ ...styles.detailLbl, color: '#0F766E' }}>Applied to workspace</td>
              <td style={styles.detailVal}>
                {apply.workspacesApplied.map((w, i) => (
                  <span key={i} style={{ fontFamily: 'monospace', fontSize: 11 }}>
                    {w.ancestor?.Description || '?'}.{w.key || '?'}
                    {i < apply.workspacesApplied.length - 1 ? ', ' : ''}
                  </span>
                ))}
              </td>
            </tr>
          )}
          {isCreated && apply.matterLocked && (
            <tr>
              <td style={{ ...styles.detailLbl, color: '#0F766E', verticalAlign: 'top' }}>
                Matter description
              </td>
              <td style={styles.detailVal}>
                <div style={{ marginBottom: 3, color: T.muted }}>
                  <span style={{ color: T.faint }}>before:</span> {apply.matterDescriptionBefore || <em style={{ color: T.faint }}>(empty)</em>}
                </div>
                <div>
                  <span style={{ color: T.faint }}>after:</span> <strong>{apply.matterDescriptionAfter || <em style={{ color: T.faint }}>(empty)</em>}</strong>
                  {apply.matterLocked === 'already-locked' && (
                    <span style={{ color: T.faint, fontSize: 11, marginLeft: 8 }}>(already had lock - description unchanged)</span>
                  )}
                </div>
              </td>
            </tr>
          )}
          {apply.appliedAt && (
            <tr>
              <td style={{ ...styles.detailLbl, color: '#0F766E' }}>Applied at</td>
              <td style={styles.detailVal}>
                <span style={{ fontFamily: 'monospace', fontSize: 11 }}>{apply.appliedAt}</span>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

function AdminRow({ r, open, onToggle, rejecting, rejectNote, setRejectNote, onStartReject, onCancelReject, onApprove, onConfirmReject, onDelete }) {
  const label = (REQUEST_TYPES.find(t => t.id === r.type)?.label) || r.type;
  return (
    <div style={{ borderBottom: `1px solid ${T.border}` }}>
      <div style={styles.adminRow} onClick={onToggle}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 2 }}>
            {label}
            <span style={styles.mineRowCM}> {r.clientNumber}.{r.matterNumber}</span>
          </div>
          <div style={{ fontSize: 12, color: T.muted }}>
            {r.requesterName || r.requesterEmail} · {fmtDate(r.createdAt)}
          </div>
        </div>
        <StatusPill status={r.status}/>
      </div>

      {open && (
        <div style={{ padding: '0 20px 20px 20px', background: '#FAFBFC' }}>
          <table style={{ borderCollapse: 'collapse', fontSize: 13, marginBottom: 12 }}>
            <tbody>
              <tr><td style={styles.detailLbl}>Requester</td><td style={styles.detailVal}>{r.requesterName} &lt;{r.requesterEmail}&gt;</td></tr>
              <tr>
                <td style={styles.detailLbl}>Client / Matter</td>
                <td style={styles.detailVal}>
                  <span style={{ fontFamily: 'monospace' }}>{r.clientNumber}.{r.matterNumber}</span>
                  {(r.clientDescription || r.matterDescription) && (
                    <span style={{ color: T.muted, marginLeft: 8 }}>
                      · {r.clientDescription || '-'}{r.matterDescription ? ` / ${r.matterDescription}` : ''}
                    </span>
                  )}
                </td>
              </tr>
              <tr><td style={styles.detailLbl}>Matter partner</td><td style={styles.detailVal}>{r.matterPartner ? (r.matterPartner.displayName || r.matterPartner.email) : '-'}</td></tr>
              {r.reason && <tr><td style={styles.detailLbl}>Reason</td><td style={styles.detailVal}>{r.reason}</td></tr>}
              <tr>
                <td style={styles.detailLbl}>People</td>
                <td style={styles.detailVal}>
                  {(r.accessGrantedTo || []).map(p => (
                    <span key={p.email} style={{ ...styles.chip, marginRight: 4, marginBottom: 4 }}>
                      {p.displayName || p.email}
                    </span>
                  ))}
                  {(!r.accessGrantedTo || r.accessGrantedTo.length === 0) && '-'}
                </td>
              </tr>
              {r.reviewedAt && (
                <>
                  <tr><td style={styles.detailLbl}>Decided by</td><td style={styles.detailVal}>{r.reviewedByName} · {fmtDate(r.reviewedAt)}</td></tr>
                  {r.decisionNote && <tr><td style={styles.detailLbl}>Note</td><td style={styles.detailVal}>{r.decisionNote}</td></tr>}
                </>
              )}
            </tbody>
          </table>

          {r.wsmApplyResult && <WsmApplyDetail apply={r.wsmApplyResult}/>}

          {r.status === 'pending' && !rejecting && (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button type="button" onClick={onApprove} style={styles.approveBtn}>Approve</button>
              <button type="button" onClick={onStartReject} style={styles.rejectBtn}>Reject</button>
              <span style={{ flex: 1 }}/>
              {onDelete && (
                <button type="button" onClick={onDelete} style={styles.deleteLink}>Delete</button>
              )}
            </div>
          )}

          {r.status === 'pending' && rejecting && (
            <div style={{ maxWidth: 640 }}>
              <textarea
                value={rejectNote}
                onChange={e => setRejectNote(e.target.value)}
                placeholder="Optional reason - included in the email to the submitter."
                rows={3}
                style={{
                  ...styles.textarea,
                  width: '100%', boxSizing: 'border-box',
                  minHeight: 80, marginBottom: 8,
                }}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" onClick={onConfirmReject} style={styles.rejectBtn}>Confirm reject</button>
                <button type="button" onClick={onCancelReject} style={styles.cancelBtn}>Cancel</button>
              </div>
            </div>
          )}

          {r.status !== 'pending' && onDelete && (
            <div style={{ marginTop: 12, display: 'flex' }}>
              <span style={{ flex: 1 }}/>
              <button type="button" onClick={onDelete} style={styles.deleteLink}>Delete this record</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
